<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import Swal from "sweetalert2"

import store from "../../state/store"
import useSliders from "../../use/useSliders"
import useDebouncedFunction from "../../use/useDebouncedFunction"

const sliders = computed(() => store.state.sliders)

const { sliderStatus, search, perPage, getSliders } = useSliders()
const { handleInput } = useDebouncedFunction(getSliders)

const emit = defineEmits(["clickEdit"])

onMounted(() => {
  getSliders()
})

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }
  getSliders(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editSlider = (slide) => {
  emit("clickEdit", slide)
}

const deleteSlider = (slide) => {
  Swal.fire({
    title: "آیا مطمئن هستید؟",
    text: "در صورت حذف شما دیگر نمی توانید آن را برگردانید!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    cancelButtonColor: "#f46a6a",
    confirmButtonText: "بله",
    cancelButtonText: "لغو",
  }).then((result) => {
    if (result.isConfirmed) {
      store.dispatch("deleteSlider", slide).then((res) => {
        console.log(res)
        Swal.fire({
          title: "حذف شد!",
          text: "اسلاید مورد نظر حذف شد.",
          icon: "success",
          confirmButtonText: "باشه",
        })
        getSliders()
      })
    }
  })
}
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی عنوان اسلاید ..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getSliders(null)"
              v-model="sliderStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت اسلایدر</option>
              <option value="0">عدم نمایش</option>
              <option value="1">نمایش</option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getSliders(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="sliderTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="slider_image">تصویر</th>
              <th class="sort" data-sort="slider_title">عنوان اسلاید</th>
              <th class="sort" data-sort="slider_link">لینک</th>
              <th class="sort" data-sort="slider_priority">اولویت</th>
              <th class="sort" data-sort="slider_status">وضعیت</th>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody class="list" v-if="sliders.loading || !sliders.data.length">
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="sliders.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ اسلایدی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(slider, index) of sliders.data" :key="index">
              <td class="slider_image">
                <div class="avatar-sm bg-light rounded p-1">
                  <img
                    :src="slider.image"
                    alt="slider.title"
                    class="img-fluid d-block"
                  />
                </div>
              </td>
              <td class="slider_title">{{ slider.title }}</td>
              <td class="slider_link">{{ slider.link }}</td>
              <td class="slider_priority">{{ slider.priority }}</td>
              <td class="slider_status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': slider.status == 1,
                    'badge-soft-danger': slider.status == 0,
                  }"
                  >{{ slider.status ? "نمایش" : "عدم نمایش" }}</span
                >
              </td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editSlider(slider)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Remove"
                  >
                    <b-link
                      class="text-danger d-inline-block remove-item-btn"
                      @click="deleteSlider(slider)"
                    >
                      <i class="ri-delete-bin-5-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="sliders.total > sliders.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of sliders.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
